import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, CheckCircle, Save, XCircle } from 'lucide-react';

const COLORS = {
  navyBlue: '#003366', orange: '#FF6B35', mustard: '#FFAA00', green: '#16A34A', red: '#DC2626',
  lightGray: '#F9FAFB', white: '#FFFFFF', borderGray: '#D1D5DB', textGray: '#6B7280', textDark: '#1F2937',
};

const fetchWithTimeout = (url, options = {}, timeoutMs = 30000) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  const token = window.sessionStorage.getItem('nwaAccessToken');
  const headers = { ...(options.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;
  return fetch(url, { ...options, headers, signal: controller.signal }).finally(() => clearTimeout(timer));
};

const todayDDMMYYYY = () => {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

const parseDate = (value) => {
  if (!value) return null;
  const s = String(value).trim();
  let y; let m; let d;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) [y, m, d] = s.split('-').map(Number);
  else if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) [d, m, y] = s.split('/').map(Number);
  else if (/^\d{2}-\d{2}-\d{4}$/.test(s)) [d, m, y] = s.split('-').map(Number);
  else return null;
  const dt = new Date(y, m - 1, d);
  return (dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d) ? dt : null;
};

const completedAgeMonths = (dobValue, screenValue) => {
  const dob = parseDate(dobValue); const screen = parseDate(screenValue);
  if (!dob || !screen || screen < dob) return null;
  let months = (screen.getFullYear() - dob.getFullYear()) * 12 + (screen.getMonth() - dob.getMonth());
  if (screen.getDate() < dob.getDate()) months -= 1;
  return months;
};

const styles = {
  container: { minHeight: '100vh', backgroundColor: COLORS.lightGray },
  header: { backgroundColor: COLORS.navyBlue, color: COLORS.white, padding: '8px 16px', borderBottom: `2px solid ${COLORS.mustard}` },
  headerContent: { maxWidth: '1100px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  backButton: { backgroundColor: 'transparent', border: 'none', color: COLORS.white, display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', padding: '8px 0' },
  body: { maxWidth: '1040px', margin: '0 auto', padding: '24px' },
  card: { backgroundColor: COLORS.white, borderRadius: '8px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', padding: '24px', marginBottom: '20px' },
  label: { display: 'block', fontSize: '14px', fontWeight: '600', color: COLORS.textDark, marginBottom: '6px' },
  input: { width: '100%', padding: '11px 14px', border: `2px solid ${COLORS.borderGray}`, borderRadius: '6px', fontSize: '16px', boxSizing: 'border-box' },
  select: { width: '100%', padding: '11px 14px', border: `2px solid ${COLORS.borderGray}`, borderRadius: '6px', fontSize: '15px', backgroundColor: COLORS.white, boxSizing: 'border-box' },
  button: { backgroundColor: COLORS.orange, color: COLORS.white, padding: '12px 24px', borderRadius: '6px', fontWeight: '600', border: 'none', cursor: 'pointer', fontSize: '16px', display: 'inline-flex', alignItems: 'center', gap: '8px' },
  errorBox: { backgroundColor: '#FEE2E2', border: '1px solid #FCA5A5', color: '#991B1B', padding: '12px 16px', borderRadius: '6px', marginBottom: '16px' },
};

const SUBTESTS = {
  word_reading: {
    title: 'Single Word Reading', shortTitle: 'Single Word Reading', rawLabel: 'Calculated raw score', rawMax: 70,
    helper: 'All 70 Blue Word Reading items are shown below and begin as correct. Click only the letters or words read incorrectly. If 10 consecutive errors are reached, the test may be terminated at that item; the system records the attempted range and calculates the raw score automatically.',
  },
  sentence_comprehension: {
    title: 'Sentence Reading Comprehension', shortTitle: 'Sentence Reading Comprehension', rawLabel: 'Calculated raw score', rawMax: 50,
    helper: 'For each sentence actually administered, mark Correct, Incorrect, or No response. Score for comprehension/meaning: spelling errors alone do not make an otherwise meaningful response incorrect. Add the exact written response when useful for intervention. Seven consecutive Incorrect and/or No response items may be used to discontinue the test.',
  },
};

export default function WRAT4Screening({ subtest, student, apiUrl, facilitatorId, facilitatorName, schoolId, schoolName, programmeId, programmeName, initialTimepoint, onBack, onSaved }) {
  const meta = SUBTESTS[subtest] || SUBTESTS.word_reading;
  const isWordReading = subtest === 'word_reading';
  const isSentenceComp = subtest === 'sentence_comprehension';
  const [config, setConfig] = useState(null);
  const [screeningDate, setScreeningDate] = useState(todayDDMMYYYY());
  const [administrationMode, setAdministrationMode] = useState('Individual');
  const administrationBatchId = ''; // kept blank internally; no practitioner entry needed
  const [assessorName, setAssessorName] = useState(facilitatorName || '');
  const [notes, setNotes] = useState('');
  const [wordErrors, setWordErrors] = useState({});
  const [terminationItem, setTerminationItem] = useState(null);
  const [sentenceResponses, setSentenceResponses] = useState({});
  const [sentenceTerminationItem, setSentenceTerminationItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [stage, setStage] = useState('form');
  const timepoint = initialTimepoint || 'Baseline';

  useEffect(() => {
    let active = true;
    fetchWithTimeout(`${apiUrl}/api/wrat4/config`)
      .then((r) => r.json().then((data) => ({ ok: r.ok, data })))
      .then(({ ok, data }) => { if (!active) return; if (!ok) throw new Error(data.error || 'Could not load reading configuration'); setConfig(data); })
      .catch((e) => { if (active) setError(e.message || 'Could not load reading configuration'); });
    return () => { active = false; };
  }, [apiUrl]);

  const ageMonths = useMemo(() => completedAgeMonths(student?.dob, screeningDate), [student?.dob, screeningDate]);
  const ageBand = useMemo(() => {
    if (ageMonths == null || !config?.age_bands) return null;
    return config.age_bands.find((b) => ageMonths >= Number(b.age_months_min) && ageMonths <= Number(b.age_months_max)) || null;
  }, [ageMonths, config]);

  const wordItems = useMemo(() => config?.word_reading_items || [], [config]);
  const consecutiveErrorLimit = Number(config?.word_reading_administration_rules?.discontinue_after_consecutive_errors || 10);
  const discontinuationEligibleItem = useMemo(() => {
    if (!isWordReading || wordItems.length === 0) return null;
    let streak = 0;
    for (const item of wordItems) {
      if (wordErrors[item.item_number]) streak += 1;
      else streak = 0;
      if (streak >= consecutiveErrorLimit) return item.item_number;
    }
    return null;
  }, [isWordReading, wordItems, wordErrors, consecutiveErrorLimit]);
  const administeredItems = useMemo(
    () => wordItems.filter((item) => !terminationItem || item.item_number <= terminationItem),
    [wordItems, terminationItem],
  );
  const incorrectItems = useMemo(() => administeredItems.filter((item) => Boolean(wordErrors[item.item_number])), [administeredItems, wordErrors]);
  const incompleteErrorItems = useMemo(
    () => incorrectItems.filter((item) => !(wordErrors[item.item_number]?.student_response || '').trim()),
    [incorrectItems, wordErrors],
  );
  const observedCorrectCount = administeredItems.length - incorrectItems.length;

  const sentenceItems = useMemo(() => config?.sentence_comprehension_items || [], [config]);
  const sentenceErrorLimit = Number(config?.sentence_comprehension_administration_rules?.discontinue_after_consecutive_noncorrect || config?.sentence_comprehension_administration_rules?.discontinue_after_consecutive_errors || 7);
  const sentenceStartLabelByItem = useMemo(() => Object.fromEntries(
    Object.entries(config?.sentence_comprehension_starting_points || {}).map(([label, itemNumber]) => [Number(itemNumber), label]),
  ), [config]);
  const attemptedSentenceRecords = useMemo(() => sentenceItems
    .filter((item) => ['Correct', 'Incorrect', 'No response'].includes(sentenceResponses[item.item_number]?.status))
    .filter((item) => !sentenceTerminationItem || item.item_number <= sentenceTerminationItem)
    .map((item) => ({ ...item, ...sentenceResponses[item.item_number] })), [sentenceItems, sentenceResponses, sentenceTerminationItem]);
  const sentenceUnscoredEnteredItems = useMemo(() => sentenceItems
    .filter((item) => (sentenceResponses[item.item_number]?.student_response || '').trim())
    .filter((item) => !['Correct', 'Incorrect'].includes(sentenceResponses[item.item_number]?.status))
    .map((item) => item.item_number), [sentenceItems, sentenceResponses]);
  const sentenceFirstAttempted = attemptedSentenceRecords.length ? attemptedSentenceRecords[0].item_number : null;
  const sentenceLastAttempted = attemptedSentenceRecords.length ? attemptedSentenceRecords[attemptedSentenceRecords.length - 1].item_number : null;
  const sentenceAttemptedNumbers = attemptedSentenceRecords.map((item) => item.item_number);
  const sentenceMissingAttemptedItems = useMemo(() => {
    if (!sentenceFirstAttempted || !sentenceLastAttempted) return [];
    const present = new Set(sentenceAttemptedNumbers);
    const missing = [];
    for (let n = sentenceFirstAttempted; n <= sentenceLastAttempted; n += 1) if (!present.has(n)) missing.push(n);
    return missing;
  }, [sentenceFirstAttempted, sentenceLastAttempted, sentenceAttemptedNumbers.join(',')]);
  const sentenceCorrectCount = attemptedSentenceRecords.filter((item) => item.status === 'Correct').length;
  const sentenceIncorrectCount = attemptedSentenceRecords.filter((item) => item.status === 'Incorrect').length;
  const sentenceNoResponseCount = attemptedSentenceRecords.filter((item) => item.status === 'No response').length;
  const sentenceCreditedPrior = sentenceFirstAttempted ? sentenceFirstAttempted - 1 : 0;
  const sentenceBasalEstablished = useMemo(() => {
    if (!sentenceFirstAttempted) return false;
    if (sentenceFirstAttempted === 1) return true;
    const byNumber = Object.fromEntries(attemptedSentenceRecords.map((item) => [item.item_number, item]));
    for (let n = sentenceFirstAttempted; n < sentenceFirstAttempted + 5; n += 1) {
      if (byNumber[n]?.status !== 'Correct') return false;
    }
    return true;
  }, [sentenceFirstAttempted, attemptedSentenceRecords]);
  const sentenceRawScore = sentenceFirstAttempted && sentenceBasalEstablished ? sentenceCreditedPrior + sentenceCorrectCount : null;
  const sentenceDiscontinuationEligibleItem = useMemo(() => {
    if (!isSentenceComp || attemptedSentenceRecords.length === 0) return null;
    let streak = 0;
    let previous = null;
    for (const item of attemptedSentenceRecords) {
      if (previous != null && item.item_number !== previous + 1) streak = 0;
      if (item.status === 'Incorrect' || item.status === 'No response') streak += 1;
      else streak = 0;
      if (streak >= sentenceErrorLimit) return item.item_number;
      previous = item.item_number;
    }
    return null;
  }, [isSentenceComp, attemptedSentenceRecords, sentenceErrorLimit]);

  const rawNum = isWordReading ? observedCorrectCount : sentenceRawScore;
  const rawValid = isWordReading
    ? wordItems.length === meta.rawMax && administeredItems.length >= 1 && Number.isInteger(rawNum) && rawNum >= 0 && rawNum <= meta.rawMax
    : sentenceItems.length === meta.rawMax && attemptedSentenceRecords.length >= 1 && sentenceMissingAttemptedItems.length === 0 && sentenceBasalEstablished && Number.isInteger(rawNum) && rawNum >= 0 && rawNum <= meta.rawMax;

  const toggleWordError = (itemNumber) => {
    setWordErrors((prev) => {
      const next = { ...prev };
      if (next[itemNumber]) delete next[itemNumber];
      else next[itemNumber] = { student_response: '' };
      return next;
    });
  };

  const setErrorResponse = (itemNumber, value) => {
    setWordErrors((prev) => ({ ...prev, [itemNumber]: { ...(prev[itemNumber] || {}), student_response: value } }));
  };

  const terminateAtEligibleItem = () => {
    if (!discontinuationEligibleItem) return;
    const stopAt = discontinuationEligibleItem;
    setTerminationItem(stopAt);
    setWordErrors((prev) => Object.fromEntries(
      Object.entries(prev).filter(([itemNumber]) => Number(itemNumber) <= stopAt),
    ));
  };


  const markSentenceItem = (itemNumber, status) => {
    setSentenceResponses((prev) => {
      const current = prev[itemNumber] || {};
      const next = { ...prev };
      if (current.status === status) {
        // Clear only the scoring choice; preserve typed evidence so staff can
        // change their judgement without having to re-enter the response.
        if ((current.student_response || '').trim()) next[itemNumber] = { ...current, status: '' };
        else delete next[itemNumber];
      } else if (status === 'No response') {
        // A genuine no-response has no written answer to retain.
        next[itemNumber] = { status: 'No response', student_response: '' };
      } else {
        next[itemNumber] = { ...current, status, student_response: current.student_response || '' };
      }
      return next;
    });
  };

  const setSentenceResponseNote = (itemNumber, value) => {
    // Allow staff to type the student's answer first, then decide Correct or
    // Incorrect after checking the scoring guide. Typing alone does not score
    // the item or count it as attempted.
    setSentenceResponses((prev) => ({ ...prev, [itemNumber]: { ...(prev[itemNumber] || {}), student_response: value } }));
  };

  const terminateSentenceAtEligibleItem = () => {
    if (!sentenceDiscontinuationEligibleItem) return;
    const stopAt = sentenceDiscontinuationEligibleItem;
    setSentenceTerminationItem(stopAt);
    setSentenceResponses((prev) => Object.fromEntries(
      Object.entries(prev).filter(([itemNumber]) => Number(itemNumber) <= stopAt),
    ));
  };


  const submit = async () => {
    if (!ageBand) { setError('The supplied norm tables cover ages 8:0 to 24:11. This student is outside that scoring range on the screening date.'); return; }
    if (isWordReading && wordItems.length !== meta.rawMax) { setError('The Single Word Reading item list has not loaded completely. Please reload before saving.'); return; }
    if (isWordReading && incompleteErrorItems.length > 0) {
      const missing = incompleteErrorItems.map((item) => `#${item.item_number} ${item.target}`).join(', ');
      setError(`Every item marked Incorrect must include what the student said so it can be used for intervention planning. Complete: ${missing}. If there was no attempt, enter "No response".`);
      return;
    }
    if (isSentenceComp && sentenceItems.length !== meta.rawMax) { setError('The Sentence Reading Comprehension scoring guide has not loaded completely. Please reload before saving.'); return; }
    if (isSentenceComp && sentenceUnscoredEnteredItems.length > 0) {
      setError(`You have entered a student response but not yet scored item${sentenceUnscoredEnteredItems.length === 1 ? '' : 's'} ${sentenceUnscoredEnteredItems.join(', ')}. Mark each as Correct or Incorrect before saving.`);
      return;
    }
    if (isSentenceComp && attemptedSentenceRecords.length === 0) { setError('Mark at least one Sentence Comprehension item as Correct, Incorrect, or No response before saving.'); return; }
    if (isSentenceComp && sentenceMissingAttemptedItems.length > 0) {
      setError(`Sentence Comprehension attempted items must be continuous from the first attempted item to the last. Please score item${sentenceMissingAttemptedItems.length === 1 ? '' : 's'} ${sentenceMissingAttemptedItems.join(', ')} or clear later responses.`);
      return;
    }
    if (isSentenceComp && !sentenceBasalEstablished) {
      setError(`Sentence Comprehension basal has not yet been established. From the lowest attempted item (${sentenceFirstAttempted}), five consecutive items must be Correct before earlier unadministered items can be credited.`);
      return;
    }
    if (!rawValid) { setError(`${meta.rawLabel} could not be calculated from the recorded administration.`); return; }
    setError(''); setLoading(true);
    try {
      const payload = {
        student_code: student.student_code, screening_date: screeningDate, timepoint, subtest, raw_score: rawNum,
        school_id: schoolId || student?.school_id || '', school_name: schoolName || '',
        programme_id: programmeId || student?.programme_id || '', programme_name: programmeName || '',
        assessor_name: assessorName, administration_mode: administrationMode, administration_batch_id: administrationBatchId,
        entered_by_user_id: facilitatorId, entered_by_name: facilitatorName, notes,
      };
      if (isWordReading) {
        payload.discontinuation_item = terminationItem || '';
        payload.items_attempted_count = administeredItems.length;
        payload.errors = incorrectItems.map((item) => ({
          item_number: item.item_number,
          student_response: wordErrors[item.item_number]?.student_response || '',
        }));
      } else if (isSentenceComp) {
        payload.discontinuation_item = sentenceTerminationItem || '';
        payload.responses = attemptedSentenceRecords.map((item) => ({
          item_number: item.item_number,
          status: item.status,
          student_response: item.student_response || '',
        }));
      }
      const res = await fetchWithTimeout(`${apiUrl}/api/wrat4/save`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
      }, 60000);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Could not save ${meta.shortTitle}`);
      setResult(data); setStage('complete'); if (onSaved) onSaved(data);
    } catch (e) {
      const message = e?.name === 'AbortError'
        ? 'The save took longer than 60 seconds. Do not press Submit again yet. Check Screening Sessions for a row with Status Saving, then retry only after confirming the previous request did not complete.'
        : (e.message || `Could not save ${meta.shortTitle}`);
      setError(message);
    }
    finally { setLoading(false); }
  };

  const Header = ({ title }) => <div style={styles.header}><div style={styles.headerContent}>
    <button style={styles.backButton} onClick={onBack}><ArrowLeft size={18} /> Back to {student.full_name}</button>
    <span style={{ fontSize: '13px', color: COLORS.mustard, fontWeight: '600' }}>{title}</span>
  </div></div>;

  if (stage === 'complete') {
    const scoring = result?.scoring;
    return <div style={styles.container}><Header title={`${meta.shortTitle}: Saved`} /><div style={styles.body}><div style={styles.card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: COLORS.green, marginBottom: '18px' }}><CheckCircle size={28} /><h2 style={{ fontSize: '20px', fontWeight: 'bold', margin: 0 }}>Saved</h2></div>
      {scoring && <div style={{ backgroundColor: '#F0FDF4', border: '1px solid #86EFAC', borderRadius: '8px', padding: '18px', marginBottom: '18px' }}>
        <div style={{ fontSize: '13px', color: COLORS.textGray }}>Age norm {scoring.age_band}</div>
        <div style={{ display: 'flex', gap: '34px', marginTop: '12px', flexWrap: 'wrap' }}>
          <div><div style={{ fontSize: '12px', color: COLORS.textGray }}>Raw score</div><div style={{ fontSize: '28px', fontWeight: '800', color: COLORS.navyBlue }}>{scoring.raw_score}</div></div>
          <div><div style={{ fontSize: '12px', color: COLORS.textGray }}>Standard score</div><div style={{ fontSize: '28px', fontWeight: '800', color: COLORS.green }}>{scoring.standard_score}</div></div>
          {scoring?.patoss?.percentile_display && <div><div style={{ fontSize: '12px', color: COLORS.textGray }}>Percentile</div><div style={{ fontSize: '28px', fontWeight: '800', color: COLORS.navyBlue }}>{scoring.patoss.percentile_display}</div></div>}
          {isWordReading && <>
            <div><div style={{ fontSize: '12px', color: COLORS.textGray }}>Attempted</div><div style={{ fontSize: '28px', fontWeight: '800', color: COLORS.navyBlue }}>{result?.items_attempted_count ?? ''}</div></div>
            <div><div style={{ fontSize: '12px', color: COLORS.textGray }}>Observed correct</div><div style={{ fontSize: '28px', fontWeight: '800', color: COLORS.navyBlue }}>{result?.observed_correct_count ?? ''}</div></div>
            <div><div style={{ fontSize: '12px', color: COLORS.textGray }}>Observed errors</div><div style={{ fontSize: '28px', fontWeight: '800', color: COLORS.red }}>{result?.observed_error_count ?? ''}</div></div>
          </>}
          {isSentenceComp && <>
            <div><div style={{ fontSize: '12px', color: COLORS.textGray }}>Total attempted</div><div style={{ fontSize: '28px', fontWeight: '800', color: COLORS.navyBlue }}>{result?.items_attempted_count ?? ''}</div></div>
            <div><div style={{ fontSize: '12px', color: COLORS.textGray }}>Unattempted</div><div style={{ fontSize: '28px', fontWeight: '800', color: COLORS.navyBlue }}>{Math.max(0, 50 - Number(result?.items_attempted_count || 0))}</div></div>
            <div><div style={{ fontSize: '12px', color: COLORS.textGray }}>Total correct</div><div style={{ fontSize: '28px', fontWeight: '800', color: COLORS.green }}>{scoring.raw_score}</div></div>
            <div><div style={{ fontSize: '12px', color: COLORS.textGray }}>Attempted but incorrect</div><div style={{ fontSize: '28px', fontWeight: '800', color: COLORS.red }}>{result?.observed_error_count ?? ''}</div></div>
            {Number(result?.credited_prior_count || 0) > 0 && <div><div style={{ fontSize: '12px', color: COLORS.textGray }}>Correct credited before start</div><div style={{ fontSize: '28px', fontWeight: '800', color: COLORS.navyBlue }}>{result?.credited_prior_count}</div></div>}
          </>}
        </div>
      </div>}
      <button style={{ ...styles.button, backgroundColor: COLORS.white, color: COLORS.navyBlue, border: `2px solid ${COLORS.navyBlue}` }} onClick={onBack}>Back to {student.full_name}</button>
    </div></div></div>;
  }

  return <div style={styles.container}><Header title={meta.shortTitle} /><div style={styles.body}>
    <div style={styles.card}>
      <h1 style={{ fontSize: '22px', fontWeight: 'bold', color: COLORS.navyBlue, margin: '0 0 6px' }}>{meta.title}</h1>
      <p style={{ color: COLORS.textGray, margin: '0 0 18px' }}>Timepoint: {timepoint}</p>
      <div style={{ backgroundColor: '#F8FAFC', border: `1px solid ${COLORS.borderGray}`, borderRadius: '8px', padding: '12px 14px', marginBottom: '18px', color: COLORS.textDark, fontSize: '14px' }}>
        {meta.helper}
      </div>
      {ageBand ? <div style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '7px 11px', borderRadius: '999px', backgroundColor: '#F0FDF4', border: '1px solid #86EFAC', color: '#15803D', fontWeight: '700', fontSize: '13px', marginBottom: '18px' }}>
        <CheckCircle size={16} /> Age norm {ageBand.label} selected
      </div> : <div style={{ ...styles.errorBox, marginBottom: '18px' }}>No supplied norm table is available for this age on the screening date.</div>}
      {error && <div style={styles.errorBox}>{error}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: '16px' }}>
        <div><label style={styles.label}>Screening date *</label><input style={styles.input} value={screeningDate} onChange={(e) => setScreeningDate(e.target.value)} placeholder="DD/MM/YYYY" /></div>
        <div>
          <label style={styles.label}>Calculated raw score</label>
          <div style={{ ...styles.input, backgroundColor: '#F0FDF4', borderColor: '#86EFAC', color: COLORS.navyBlue, fontWeight: '800' }}>{rawNum == null ? '—' : rawNum} / {meta.rawMax}</div>
        </div>
        <div><label style={styles.label}>Administration mode</label><select style={styles.select} value={administrationMode} onChange={(e) => setAdministrationMode(e.target.value)}><option>Individual</option><option>Group</option></select></div>
        <div><label style={styles.label}>Assessor</label><input style={styles.input} value={assessorName} onChange={(e) => setAssessorName(e.target.value)} /></div>
      </div>

      {isWordReading && administeredItems.length > 0 && <div style={{ marginTop: '22px', borderTop: `1px solid ${COLORS.borderGray}`, paddingTop: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '12px' }}>
          <div>
            <h2 style={{ fontSize: '18px', color: COLORS.navyBlue, margin: '0 0 3px' }}>Observed item record</h2>
            <div style={{ fontSize: '13px', color: COLORS.textGray }}>All 70 items begin as correct. Click only the letters or words the student reads incorrectly.</div>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <span style={{ padding: '6px 10px', borderRadius: '999px', backgroundColor: '#EFF6FF', color: '#1D4ED8', fontWeight: '700', fontSize: '13px' }}>{administeredItems.length}/70 attempted</span>
            <span style={{ padding: '6px 10px', borderRadius: '999px', backgroundColor: '#F0FDF4', color: '#15803D', fontWeight: '700', fontSize: '13px' }}>{observedCorrectCount} observed correct</span>
            <span style={{ padding: '6px 10px', borderRadius: '999px', backgroundColor: '#FEF2F2', color: '#B91C1C', fontWeight: '700', fontSize: '13px' }}>{incorrectItems.length} incorrect</span>
          </div>
        </div>

        {discontinuationEligibleItem && !terminationItem && <div style={{ marginBottom: '14px', padding: '12px 14px', backgroundColor: '#FFF7ED', border: '1px solid #FDBA74', borderRadius: '8px', color: '#9A3412' }}>
          <div style={{ fontWeight: '800', marginBottom: '4px' }}>{consecutiveErrorLimit} consecutive errors reached at item {discontinuationEligibleItem}.</div>
          <div style={{ fontSize: '13px', marginBottom: '10px' }}>The test may be terminated here. If you continue, keep marking later items as usual.</div>
          <button type="button" onClick={terminateAtEligibleItem} style={{ ...styles.button, padding: '8px 14px', fontSize: '14px' }}>Terminate at item {discontinuationEligibleItem}</button>
        </div>}

        {terminationItem && <div style={{ marginBottom: '14px', padding: '12px 14px', backgroundColor: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: '8px', color: '#991B1B' }}>
          <div style={{ fontWeight: '800' }}>Administration terminated at item {terminationItem} after {consecutiveErrorLimit} consecutive errors.</div>
          <div style={{ fontSize: '13px', marginTop: '3px' }}>Items {terminationItem + 1}-70 are recorded as not attempted and are not counted as correct.</div>
        </div>}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(135px,1fr))', gap: '8px' }}>
          {administeredItems.map((item) => {
            const incorrect = Boolean(wordErrors[item.item_number]);
            return <button key={item.item_number} type="button" onClick={() => toggleWordError(item.item_number)} style={{
              minHeight: '60px', borderRadius: '8px', border: `2px solid ${incorrect ? '#FCA5A5' : '#86EFAC'}`,
              backgroundColor: incorrect ? '#FEF2F2' : '#F0FDF4', color: incorrect ? '#991B1B' : '#166534',
              cursor: 'pointer', textAlign: 'left', padding: '8px 10px', fontWeight: '700',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '11px', opacity: 0.75 }}>#{item.item_number}</span>
                {incorrect ? <XCircle size={15} /> : <CheckCircle size={15} />}
              </div>
              <div style={{ fontSize: item.item_type === 'letter' ? '18px' : '14px', marginTop: '4px', overflowWrap: 'anywhere' }}>{item.target}</div>
            </button>;
          })}
        </div>

        {incorrectItems.length > 0 && <div style={{ marginTop: '18px', padding: '16px', backgroundColor: '#FFF7ED', border: '1px solid #FDBA74', borderRadius: '8px' }}>
          <div style={{ fontWeight: '700', color: COLORS.textDark, marginBottom: '4px' }}>Incorrect items — student response required</div>
          <div style={{ fontSize: '13px', color: COLORS.textGray, marginBottom: '10px' }}>Enter what the student actually said for every incorrect item. If the student made no attempt, enter "No response". These responses are retained for intervention planning and report writing.</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))', gap: '10px' }}>
            {incorrectItems.map((item) => {
              const missingResponse = !(wordErrors[item.item_number]?.student_response || '').trim();
              return <div key={`err-${item.item_number}`}>
                <label style={styles.label}>#{item.item_number} {item.target} *</label>
                <input
                  style={{ ...styles.input, borderColor: missingResponse ? '#F59E0B' : COLORS.borderGray }}
                  value={wordErrors[item.item_number]?.student_response || ''}
                  onChange={(e) => setErrorResponse(item.item_number, e.target.value)}
                  placeholder="What the student said (required)"
                />
              </div>;
            })}
          </div>
          <div style={{ marginTop: '10px', fontSize: '13px', fontWeight: '700', color: incompleteErrorItems.length ? '#9A3412' : '#166534' }}>
            {incompleteErrorItems.length ? `${incompleteErrorItems.length} incorrect item response${incompleteErrorItems.length === 1 ? '' : 's'} still required` : 'All incorrect-item responses captured ✓'}
          </div>
        </div>}

        <div style={{ marginTop: '12px', fontSize: '13px', color: COLORS.textGray }}>
          Correct items are not written as separate response rows. The saved attempted range plus the fixed item list and error rows allow the system to reconstruct both attempted-correct and attempted-incorrect words later for reporting and intervention.
        </div>
      </div>}

      {isSentenceComp && sentenceItems.length > 0 && <div style={{ marginTop: '22px', borderTop: `1px solid ${COLORS.borderGray}`, paddingTop: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '12px' }}>
          <div>
            <h2 style={{ fontSize: '18px', color: COLORS.navyBlue, margin: '0 0 3px' }}>Attempted item record</h2>
            <div style={{ fontSize: '13px', color: COLORS.textGray, maxWidth: '760px' }}>
              Record items administered as Correct, Incorrect, or No response. Consider score for comprehension and meaning: a spelling error should not be scored as incorrect. Transfer the exact student response using brackets to correct words that may be wrongly spelt.
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <span style={{ padding: '6px 10px', borderRadius: '999px', backgroundColor: '#EFF6FF', color: '#1D4ED8', fontWeight: '700', fontSize: '13px' }}>{attemptedSentenceRecords.length}/50 attempted</span>
            <span style={{ padding: '6px 10px', borderRadius: '999px', backgroundColor: '#F3F4F6', color: '#4B5563', fontWeight: '700', fontSize: '13px' }}>{50 - attemptedSentenceRecords.length} unattempted</span>
            <span style={{ padding: '6px 10px', borderRadius: '999px', backgroundColor: '#F0FDF4', color: '#15803D', fontWeight: '700', fontSize: '13px' }}>{sentenceCorrectCount} correct</span>
            <span style={{ padding: '6px 10px', borderRadius: '999px', backgroundColor: '#FEF2F2', color: '#B91C1C', fontWeight: '700', fontSize: '13px' }}>{sentenceIncorrectCount} incorrect</span>
            <span style={{ padding: '6px 10px', borderRadius: '999px', backgroundColor: '#FFF7ED', color: '#B45309', fontWeight: '700', fontSize: '13px' }}>{sentenceNoResponseCount} no response</span>
            {sentenceFirstAttempted && sentenceBasalEstablished && <span style={{ padding: '6px 10px', borderRadius: '999px', backgroundColor: '#F8FAFC', color: COLORS.navyBlue, fontWeight: '700', fontSize: '13px' }}>{sentenceCreditedPrior} earlier items credited</span>}
          </div>
        </div>

        {sentenceMissingAttemptedItems.length > 0 && <div style={{ marginBottom: '14px', padding: '10px 12px', backgroundColor: '#FFF7ED', border: '1px solid #FDBA74', borderRadius: '8px', color: '#9A3412', fontSize: '13px' }}>
          Attempted items must be continuous. Still unscored between the first and last attempted item: {sentenceMissingAttemptedItems.join(', ')}.
        </div>}

        {sentenceFirstAttempted && sentenceFirstAttempted > 1 && !sentenceBasalEstablished && <div style={{ marginBottom: '14px', padding: '10px 12px', backgroundColor: '#FFF7ED', border: '1px solid #FDBA74', borderRadius: '8px', color: '#9A3412', fontSize: '13px' }}>
          Basal not yet established. From the lowest attempted item ({sentenceFirstAttempted}), five consecutive items must be marked Correct. If this is not achieved at the planned starting point, continue the official reverse administration until five consecutive correct responses are established.
        </div>}

        {sentenceDiscontinuationEligibleItem && !sentenceTerminationItem && <div style={{ marginBottom: '14px', padding: '12px 14px', backgroundColor: '#FFF7ED', border: '1px solid #FDBA74', borderRadius: '8px', color: '#9A3412' }}>
          <div style={{ fontWeight: '800', marginBottom: '4px' }}>{sentenceErrorLimit} consecutive Incorrect and/or No response items reached at item {sentenceDiscontinuationEligibleItem}.</div>
          <div style={{ fontSize: '13px', marginBottom: '10px' }}>The test may be discontinued here. If you continue, keep scoring later attempted items as usual.</div>
          <button type="button" onClick={terminateSentenceAtEligibleItem} style={{ ...styles.button, padding: '8px 14px', fontSize: '14px' }}>Discontinue at item {sentenceDiscontinuationEligibleItem}</button>
        </div>}

        {sentenceTerminationItem && <div style={{ marginBottom: '14px', padding: '12px 14px', backgroundColor: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: '8px', color: '#991B1B' }}>
          <div style={{ fontWeight: '800' }}>Administration discontinued at item {sentenceTerminationItem} after {sentenceErrorLimit} consecutive Incorrect and/or No response items.</div>
          <div style={{ fontSize: '13px', marginTop: '3px' }}>Later items are recorded as not attempted.</div>
        </div>}

        <div style={{ display: 'grid', gap: '8px' }}>
          {sentenceItems
            .filter((item) => !sentenceTerminationItem || item.item_number <= sentenceTerminationItem)
            .map((item) => {
              const response = sentenceResponses[item.item_number];
              const correct = response?.status === 'Correct';
              const incorrect = response?.status === 'Incorrect';
              const noResponse = response?.status === 'No response';
              const startLabel = sentenceStartLabelByItem[item.item_number];
              return <React.Fragment key={item.item_number}>
                {startLabel && <div style={{ marginTop: item.item_number === 1 ? '0' : '10px', padding: '5px 10px', backgroundColor: '#EFF6FF', color: COLORS.navyBlue, borderRadius: '6px', fontSize: '12px', fontWeight: '800' }}>Starting point {startLabel} · item {item.item_number}</div>}
                <div style={{ position: 'relative', border: `1px solid ${COLORS.borderGray}`, borderRadius: '10px', padding: '22px 14px 12px', backgroundColor: response ? '#FFFFFF' : '#FAFAFA', textAlign: 'left' }}>
                  <div style={{ position: 'absolute', top: '7px', left: '10px', fontSize: '10px', fontWeight: '800', letterSpacing: '0.03em', color: COLORS.textGray }}>Item {item.item_number}</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'minmax(360px,1fr) auto', gap: '18px', alignItems: 'center', textAlign: 'left' }}>
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ fontSize: '14px', lineHeight: 1.45, color: COLORS.navyBlue, fontWeight: '600', textAlign: 'left' }}>{item.prompt || `Sentence Comprehension item ${item.item_number}`}</div>
                    </div>
                    <div style={{ display: 'flex', gap: '7px', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-end' }}>
                      <button type="button" onClick={() => markSentenceItem(item.item_number, 'Correct')} style={{ border: `2px solid ${correct ? '#16A34A' : '#BBF7D0'}`, backgroundColor: correct ? '#DCFCE7' : '#F0FDF4', color: '#166534', borderRadius: '999px', padding: '7px 12px', fontWeight: '800', cursor: 'pointer' }}>✓ Correct</button>
                      <button type="button" onClick={() => markSentenceItem(item.item_number, 'Incorrect')} style={{ border: `2px solid ${incorrect ? '#DC2626' : '#FECACA'}`, backgroundColor: incorrect ? '#FEE2E2' : '#FEF2F2', color: '#991B1B', borderRadius: '999px', padding: '7px 12px', fontWeight: '800', cursor: 'pointer' }}>✕ Incorrect</button>
                      <button type="button" onClick={() => markSentenceItem(item.item_number, 'No response')} style={{ border: `2px solid ${noResponse ? '#D97706' : '#FED7AA'}`, backgroundColor: noResponse ? '#FFEDD5' : '#FFF7ED', color: '#9A3412', borderRadius: '999px', padding: '7px 12px', fontWeight: '800', cursor: 'pointer' }}>No response</button>
                    </div>
                  </div>
                  {!noResponse && <div style={{ marginTop: '10px', textAlign: 'left' }}>
                    <input style={{ ...styles.input, padding: '8px 10px', fontSize: '14px', textAlign: 'center' }} value={response?.student_response || ''} onChange={(e) => setSentenceResponseNote(item.item_number, e.target.value)} placeholder="Type exact student response, then mark Correct or Incorrect" />
                    {(response?.student_response || '').trim() && !correct && !incorrect && <div style={{ marginTop: '5px', fontSize: '11px', color: '#B45309', textAlign: 'center' }}>Response entered — choose Correct or Incorrect.</div>}
                  </div>}
                  <details style={{ marginTop: '8px', fontSize: '12px', color: COLORS.textGray, textAlign: 'left' }}>
                    <summary style={{ cursor: 'pointer', color: COLORS.navyBlue, fontWeight: '700', textAlign: 'left' }}>Scoring guide</summary>
                    <div style={{ marginTop: '6px', lineHeight: 1.45, textAlign: 'left' }}><strong>Accept:</strong> {(item.correct_responses || []).join(', ')}</div>
                    {(item.known_incorrect_responses || []).length > 0 && <div style={{ marginTop: '3px', lineHeight: 1.45, textAlign: 'left' }}><strong>Known incorrect:</strong> {item.known_incorrect_responses.join(', ')}</div>}
                  </details>
                </div>
              </React.Fragment>;
            })}
        </div>

      </div>}

      <div style={{ marginTop: '16px' }}><label style={styles.label}>Notes (optional)</label><textarea style={{ ...styles.input, minHeight: '90px', resize: 'vertical' }} value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
      <div style={{ marginTop: '20px' }}>
        {error && <div style={{ ...styles.errorBox, marginBottom: '12px' }}>{error}</div>}
        <button style={{ ...styles.button, opacity: loading ? 0.6 : 1 }} onClick={submit} disabled={loading}><Save size={19} /> {loading ? 'Saving...' : 'Submit & Lock'}</button>
      </div>
    </div>
  </div></div>;
}
